<template>
  <div>
    <v-row>
      <v-col>
        <v-toolbar flat>
          <v-toolbar-title>
            Varekategorier
          </v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            solo
            hide-details
            dense
            clearable
            class="mr-3"
            append-icon="mdi-magnify"
          />
          <v-spacer />
          <v-menu
            v-model="addMenu"
            left
            :close-on-content-click="false"
            min-width="500"
          >
            <template #activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                v-on="on"
              >
                Ny varekategori
                <v-icon right> mdi-plus </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                Ny varekategori
              </v-card-title>
              <v-card-text>
                <v-form
                  ref="form"
                  v-model="valid"
                >
                  <v-text-field
                    v-model="newItem.title"
                    label="Navn"
                    autofocus
                    :rules="[ v => !!v || 'Udfyld et navn' ]"
                  />
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  @click="addMenu = false;"
                >
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  :disabled="!valid"
                  :loading="creating"
                  @click="addItem"
                >
                  Opret
                  <v-icon right> mdi-plus </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-toolbar>
        <v-data-table
          :items="items"
          :headers="headers"
          :loading="loading"
          :search="search"
          :items-per-page="15"
          :footer-props="{ 'items-per-page-options': [15, 30, 50, 75, -1] }"
        >
          <template #[`item.profitFactor`]="{ value }">
            {{ typeof value === 'number' && value !== 0 ? value * 100 + '%' : '' }}
          </template>
          <template #[`item.discountFactor`]="{ value }">
            {{ typeof value === 'number' && value !== 0 ? value * 100 + '%' : '' }}
          </template>
          <template #[`item.createdOn`]="{ value }">
            {{ value | formatDateTime }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn small @click="onEditItem(item)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              autofocus
              outlined
              :rules="[ v => !!v || 'Udfyld et navn' ]"
            />
            <v-text-field
              v-model="editItem.code"
              label="Kode"
              autofocus
              outlined
            />
            <v-text-field
              :value="editItem.profitFactor * 100"
              @input="(value) => editItem.profitFactor = value / 100"
              label="Profitfaktor"
              prefix="Varens kostpris +"
              persistent-hint
              autofocus
              outlined
              :rules="[ v => typeof v === 'number' || 'Profitfaktor skal udfyldes' ]"
              suffix="%"
            />
            <v-text-field
              :value="editItem.discountFactor * 100"
              @input="(value) => editItem.discountFactor = value / 100"
              label="Rabatfaktor"
              prefix="Varens listepris -"
              hint="Ignoreres hvis profitfaktor ikke er 0"
              persistent-hint
              prepend="Test"
              autofocus
              outlined
              :rules="[ v => typeof v === 'number' || 'Rabatfaktor skal udfyldes' ]"
              suffix="%"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />

          <v-btn :loading="saving" @click="saveItem()">
            {{ $t('common.save') }}
            <v-icon right> mdi-content-save-outline </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const baseUrl = 'customer-part-categories';

export default {
  name: 'CustomerPartCategories',
  props: ['customer'],
  data() {
    return {
      loading: true,
      creating: false,
      saving: false,
      addMenu: false,
      editDialog: false,
      editItem: {},
      valid: false,
      editForm: false,
      newItem: {},
      items: [],
      headers: [
        { text: 'Navn', value: 'title', width: '30%' },
        { text: 'Kode', value: 'code' },
        { text: 'Profitfaktor', value: 'profitFactor', align: 'right' },
        { text: 'Rabatfaktor', value: 'discountFactor', align: 'right' },
        { text: 'Oprettet', value: 'createdOn', align: 'right' },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false }
      ],
      search: '',
    }
  },
  mounted: async function() {
    this.getItems();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        setTimeout(() => {
          this.editItem = {};
        }, 200);
      }
    }
  },
  methods: {
    addItem: async function() {
      this.creating = true;

      try {
        const model = {
          ...this.newItem,
          customerId: this.$route.params.customerId
        };

        const { data } = await this.$api.post(baseUrl, model);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Varekategori oprettet' });
        this.getItems();
        this.clear();
        this.addMenu = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.creating = false;
      }
    },
    onEditItem: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editDialog = true;
    },
    saveItem: async function() {
      this.saving = true;

      try {
        const { data } = await this.$api.patch(`${baseUrl}/${this.editItem.id}`, this.editItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Varekategori opdateret' });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    clear: function() {
      this.newItem = {};
    },
    getItems: async function() {
      this.loading = true;

      try {
        const { data } = await this.$api.get(baseUrl, {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.items = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>